/**
 * Central platform / API-origin boundary for web + future Capacitor builds.
 * Do not scatter Capacitor / protocol checks across components — use this module.
 */

type CapacitorBridge = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
};

type CapacitorWindow = Window & {
  Capacitor?: CapacitorBridge;
};

function getCapacitor(): CapacitorBridge | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as CapacitorWindow).Capacitor;
}

/** True when running inside a Capacitor native shell. */
export function isNativePlatform(): boolean {
  try {
    return Boolean(getCapacitor()?.isNativePlatform?.());
  } catch {
    return false;
  }
}

/** True when the native platform is iOS. */
export function isIOS(): boolean {
  try {
    const platform = getCapacitor()?.getPlatform?.();
    if (platform) return platform === 'ios';
  } catch {
    // fall through
  }
  return isNativePlatform() && /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * Public API origin for compiled native builds.
 * Web may leave this empty and use same-origin `/api`.
 */
const rawBase = import.meta.env.VITE_API_BASE_URL;
export const API_BASE_URL =
  typeof rawBase === 'string' ? rawBase.replace(/\/$/, '') : '';

/**
 * Native production Vite builds set VITE_NATIVE_BUILD=1 and must provide an HTTPS API base.
 * Do not throw here — a module-level throw blanks the Capacitor WebView.
 * Missing config is collected in startupConfig and shown on screen.
 */

/** Resolve a MovieMe-owned API path against the configured base (or same-origin). */
export function apiUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalized}`;
}

/** Mark the document root for native-only CSS (safe-area, etc.). */
export function applyNativeDocumentClass(): void {
  if (typeof document === 'undefined') return;
  if (!isNativePlatform()) return;
  document.documentElement.dataset.platform = isIOS() ? 'ios-native' : 'native';
}

/** Hide the native splash once the web shell can paint. No-op on web. */
export async function hideNativeSplash(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch {
    // plugin unavailable
  }
}
